// global Config */

// Vendor Libs
import {cloneDeep, filter, get, isEmpty, mapValues, merge, omit, set, uniq, unset} from 'lodash';

// Config
import getBootstrapInitialState from '../bootstrap-data/forms-initial-states';
import {applyPreferenceState} from '../../config/preferences-config';

let preferenceStates = {};

const getFormInitialState = (formName) => {
    const state = getBootstrapInitialState(formName);
    const preferenceState = preferenceStates[formName];

    return applyPreferenceState('formData', preferenceState, state, formName);
};

const getFormNewState = (state, formName) => applyPreferenceState(
        'formData',
        preferenceStates[formName],
        get(state, formName, getBootstrapInitialState(formName)),
        formName
    );

const actions = {
    FORM_REGISTER: (state, {formName}) => {
        const newState = cloneDeep(state);

        return {
            ...newState,
            [formName]: getFormNewState(newState, formName),
        };
    },

    FORM_UNREGISTER: (state, {formName}) => omit(cloneDeep(state), formName),

    FORM_SET_FIELD_VALUE: (state, {formName, fieldName, operator, value}) => {
        const newValue = value !== '' ? value : undefined;

        return set(
            set(cloneDeep(state), `${formName}.values.${fieldName}`, newValue),
            `${formName}.operators.${fieldName}`,
            operator
        );
    },

    FORM_SET_FIELD_VALUES: (state, {formName, values, resetValues}) => {
        const newState = cloneDeep(state);
        const formValues = resetValues ? values : {...get(newState, `${formName}.values`), ...values};

        return set(newState, `${formName}.values`, formValues);
    },

    SET_FORM_CUSTOM_PATH: (state, {formName, customPath}) => set(cloneDeep(state), `${formName}.customPath`, customPath),
    FORM_RESET: (state, {formName, resetStore, resetValues, submitted}) => {
        let newState;
        const visibleFields = get(state, [formName, 'visibleFields'], []);

        if (formName) {
            newState = set(
                cloneDeep(state),
                formName,
                resetValues ? getBootstrapInitialState(formName) : getFormInitialState(formName)
            );

            if (!isEmpty(visibleFields)) {
                set(newState, [formName, 'visibleFields'], visibleFields);
            }

            set(newState, [formName, 'submitted'], submitted);
        } else if (!resetStore) {
            newState = mapValues(state, (_value, formName) => getBootstrapInitialState(formName));
        } else {
            newState = {};
        }

        return newState;
    },

    FORM_UNREGISTER_FIELD_VALUE: (state, {formName, fieldName}) => {
        const newState = cloneDeep(state);

        unset(newState, `${formName}.values.${fieldName}`);
        return newState;
    },

    FORM_SET_FIELD_ERRORS: (state, {formName, fieldName, errors}) => {
        const newState = cloneDeep(state);

        return {
            ...newState,
            [formName]: {
                ...newState[formName],
                fieldErrors: {
                    ...newState[formName].fieldErrors,
                    [fieldName]: errors,
                },
            },
        };
    },

    FORM_SUBMITTED: (state, {formName, submitted}) => set(cloneDeep(state), `${formName}.submitted`, submitted),

    FORM_UNREGISTER_FIELD_ERRORS: (state, {formName, fieldName}) => {
        const newState = cloneDeep(state);

        unset(newState, [formName, 'fieldErrors', fieldName]);
        return newState;
    },

    FORM_UNREGISTER_FORM_ERRORS: (state, {formName}) =>
        set(cloneDeep(state), `${formName}.formErrors`, getFormInitialState(formName).formErrors),
    FORM_SUBMIT: (state, {formName}) => {
        const newState = cloneDeep(state);

        return {
            ...newState,
            [formName]: {
                ...newState[formName],
                fetched: false,
                fetching: true,
                formErrors: null,
                location: null,
                response: null,
                submitted: false,
            },
        };
    },

    FORM_SUBMIT_FULFILLED: (state, {formName, response}) => merge(cloneDeep(state), {[formName]: {fetched: true, fetching: false, response}}),

    FORM_SUBMIT_REJECTED: (state, {formName, formErrors}) => merge(cloneDeep(state), {[formName]: {fetched: true, fetching: false, formErrors}}),
    FORM_SUBMIT_RESET: (state, {formName, resetValues}) => {
        let newState = cloneDeep(state);
        if (resetValues) {
            newState = set(cloneDeep(state), formName, set(getFormInitialState(formName), 'submitted', false));
        } else {
            newState = merge(cloneDeep(state), {
                [formName]: {fetched: false, fetching: false, formErrors: null, location: null, response: null},
            });
        }

        return newState;
    },

    FORM_SUBMIT_ASYNC_LOCATION: (state, {formName, location}) => {
        const newState = cloneDeep(state);

        return set(newState, [formName, 'location'], location);
    },

    PREFERENCES_LOAD: (state, {matureStores, stores}) => {
        preferenceStates = get(Config.base.matureContent ? matureStores : stores, 'formData', {});

        const newState = mapValues(state, (value, formName) => {
            const visibleFields = get(preferenceStates, [formName, 'visibleFields']);

            return visibleFields
                ? {
                      ...value,
                      visibleFields,
                  }
                : value;
        });

        return cloneDeep(newState);
    },

    PREFERENCES_SAVE: (state, {matureStores, stores}) => {
        preferenceStates = get(Config.base.matureContent ? matureStores : stores, 'formData', {});

        const newState = mapValues(state, (value, formName) => {
            const visibleFields = get(preferenceStates, [formName, 'visibleFields']);

            return visibleFields
                ? {
                      ...value,
                      visibleFields,
                  }
                : value;
        });

        return cloneDeep(newState);
    },

    PREFERENCES_RESET: (state) => {
        preferenceStates = {};

        const newState = mapValues(state, (value, formName) => {
            const visibleFields = get(getBootstrapInitialState(formName), 'visibleFields');

            return visibleFields
                ? {
                      ...value,
                      visibleFields,
                  }
                : value;
        });

        return cloneDeep(newState);
    },

    SEARCH_FORM_REMOVE_FIELD: (state, {formName, fieldName}) => {
        const visibleFields = filter(get(state, [formName, 'visibleFields'], []), (val) => val !== fieldName);

        return set(
            cloneDeep(state),
            [formName, 'visibleFields'],
            !isEmpty(visibleFields) ? visibleFields : getBootstrapInitialState(formName).visibleFields
        );
    },

    SEARCH_FORM_ADD_FIELD: (state, {formName, fieldName}) => set(cloneDeep(state), [formName, 'visibleFields'], [...get(state, [formName, 'visibleFields'], []), fieldName]),

    SEARCH_FORM_ADD_FIELDS: (state, {formName, fieldNames}) => set(
            cloneDeep(state),
            [formName, 'visibleFields'],
            uniq([...get(state, [formName, 'visibleFields'], []), ...fieldNames])
        ),

    SEARCH_FORM_RESET_FIELDS: (state, {formName}) => set(cloneDeep(state), [formName, 'visibleFields'], getBootstrapInitialState(formName).visibleFields),
};

const reducer = (state = {}, action) => (actions[action.type] ? actions[action.type](state, action) : state);

export default reducer;
